import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import type { ParsedUrlQuery } from 'querystring';

import MetaTags from 'Components/MetaTags/MetaTags';
import PageComposer from 'Components/PageComposer/PageComposer';
import { InvalidVehicle } from 'Components/pages/VehicleDetailsRedesign/Components/InvalidVehicle/InvalidVehicle';
import { SimilarVehiclesProvider } from 'Components/pages/VehicleDetailsRedesign/sections/SimilarVehiclesSection/SimilarVehiclesProvider/SimilarVehiclesProvider';
import { VehicleDetailsRedesign } from 'Components/pages/VehicleDetailsRedesign/VehicleDetailsRedesign';
import { VehicleDataProvider } from 'Context/VehicleContext/VehicleDataContext';
import { logger } from 'Services/logger/logger';
import { PAGE_SLUGS } from 'Utilities/consts';

import { getMetaTags } from './helpers/getMetaTags';
import { getRedirectConfig } from './helpers/getRedirectConfig';
import type { GetVehicleDetailsResponseError } from './helpers/getVehicleDetails';
import { getVehicleDetails } from './helpers/getVehicleDetails';
import type { VehicleData } from './helpers/vehicle.schema';

interface PageParams extends ParsedUrlQuery {
	enquiryId: string;
}

export type VehiclePageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

interface GetServerSideData {
	error: GetVehicleDetailsResponseError;
	vehicle: Nullable<VehicleData>;
}

export const getServerSideProps: GetServerSideProps<GetServerSideData> = async (context) => {
	// TODO Consider adding cache control headers - https://motorway.atlassian.net/browse/DEP-54
	const { params, req, resolvedUrl } = context;
	const { user } = req;

	if (!user) {
		return getRedirectConfig({ isAuth: false, route: resolvedUrl });
	}

	const { dealerId, id: dealerContactId, partners } = user;
	const { enquiryId } = params as PageParams;
	const { mockScenario } = req.cookies;

	try {
		const { error, vehicle } = await getVehicleDetails({
			dealerContactId,
			dealerId,
			mockScenario,
			partners: partners || [],
			vehicleId: enquiryId,
		});

		return {
			props: {
				error,
				vehicle,
			},
		};
	} catch (error) {
		logger.error({
			message: 'There was an issue fetching vehicle details',
			scope: 'VehicleDetailsPage',
		});
		return {
			notFound: true,
		};
	}
};

type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const Page: React.FC<PageProps> = ({ error, vehicle }) => {
	const { description, title } = getMetaTags(vehicle);
	const { description: errorDescription, isValidVehicle, title: errorTitle } = error;

	const canDisplayVehicle = vehicle && isValidVehicle;

	return (
		<>
			{/* @ts-ignore */}
			<MetaTags noIndex description={description} title={title} />
			{/* Add a key so that all components will unmount on route change */}
			<PageComposer key={vehicle?.id} pageSlug={PAGE_SLUGS.VEHICLE}>
				{canDisplayVehicle ? (
					<VehicleDataProvider vehicle={vehicle}>
						<SimilarVehiclesProvider>
							<VehicleDetailsRedesign />
						</SimilarVehiclesProvider>
					</VehicleDataProvider>
				) : (
					<InvalidVehicle description={errorDescription} title={errorTitle} />
				)}
			</PageComposer>
		</>
	);
};

export default Page;
